import {
  IRynReplayNetworkEntry,
  IRynReplayTimelineMarker,
  toRynReplayOffsetMs,
} from './ryn-replay-devtools';

/**
 * App Insights rows for one captured request, as the read API projects them.
 * Kept tolerant: a field the function adds later must degrade, not drop the row.
 */

export type RynReplayBackendStatus =
  | 'ok'
  | 'empty'
  | 'notConfigured'
  | 'unavailable';

export type RynReplayBackendItemType =
  | 'request'
  | 'exception'
  | 'trace'
  | 'dependency'
  | string;

export interface IRynReplayBackendEvent {
  timestamp: string;
  itemType: RynReplayBackendItemType;
  operationId: string;
  name?: string | null;
  message?: string | null;
  resultCode?: string | null;
  durationMs?: number | null;
  dependencyType?: string | null;
  target?: string | null;
  success?: boolean | null;
  severityLevel?: number | null;
}

export interface IRynReplayBackendOperation {
  operationId: string;
  events: IRynReplayBackendEvent[];
}

export interface IRynReplayBackendTelemetry {
  status: RynReplayBackendStatus;
  operations: IRynReplayBackendOperation[];
}

export const collectRynReplayOperationIds = (
  entries: IRynReplayNetworkEntry[]
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const id = entry.operationId;
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export const filterRynReplayBackendOperations = (
  operations: IRynReplayBackendOperation[],
  operationId: string | undefined
): IRynReplayBackendOperation[] => {
  if (!operationId) {
    return operations;
  }
  return operations.filter((op) => op.operationId === operationId);
};

const BACKEND_ERROR_ITEM_TYPES = new Set(['exception']);

/**
 * True when this AI row should bookmark the scrub bar. Exceptions always;
 * traces at Error/Critical; failed requests. Dependencies stay off the bar
 * (they are the waterfall inside the tab).
 */
export const isRynReplayBackendErrorEvent = (
  event: IRynReplayBackendEvent
): boolean => {
  if (BACKEND_ERROR_ITEM_TYPES.has(event.itemType)) {
    return true;
  }
  if (event.itemType === 'trace' && (event.severityLevel ?? 0) >= 3) {
    return true;
  }
  if (event.itemType === 'request' && event.success === false) {
    return true;
  }
  return false;
};

export const backendEventSummary = (event: IRynReplayBackendEvent): string => {
  if (event.itemType === 'exception') {
    return event.name || event.message || 'Server exception';
  }
  if (event.itemType === 'request') {
    return `${event.name ?? 'request'} → ${event.resultCode ?? 'failed'}`;
  }
  return event.message || event.name || event.itemType;
};

export const deriveRynReplayBackendMarkers = (
  events: IRynReplayBackendEvent[],
  baselineTs: number,
  totalMs: number
): IRynReplayTimelineMarker[] => {
  const markers: IRynReplayTimelineMarker[] = [];
  for (const event of events) {
    if (!isRynReplayBackendErrorEvent(event)) {
      continue;
    }
    const ts = Date.parse(event.timestamp);
    if (!Number.isFinite(ts)) {
      continue;
    }
    markers.push({
      offsetMs: toRynReplayOffsetMs(ts, baselineTs, totalMs),
      category: 'backend-error',
      summary: backendEventSummary(event),
    });
  }
  markers.sort((a, b) => a.offsetMs - b.offsetMs);
  return markers;
};

export const flattenRynReplayBackendEvents = (
  operations: IRynReplayBackendOperation[]
): IRynReplayBackendEvent[] => operations.flatMap((op) => op.events);

export const describeRynReplayBackendStatus = (
  status: RynReplayBackendStatus,
  hasOperationIds: boolean
): string => {
  if (!hasOperationIds) {
    return 'This recording was captured before request tracing. Play a newer session to see what the server did with each call.';
  }
  switch (status) {
    case 'notConfigured':
      return 'Server telemetry is not configured for this environment. The recording still plays.';
    case 'unavailable':
      return 'Application Insights could not be queried. Try again in a moment.';
    case 'empty':
      return 'No server telemetry was found for these requests. It may have aged out of App Insights retention.';
    default:
      return '';
  }
};
